import React from 'react';
import { Draggable } from '@hello-pangea/dnd';
import { cn } from '../../lib/utils';
import { Calendar, MessageSquare } from 'lucide-react';
import PriorityBadge from './PriorityBadge';
import type { TaskItemDto } from '../../types/tasks';

interface TaskCardProps {
  task: TaskItemDto;
  index: number;
  onClick: (task: TaskItemDto) => void;
}

function getInitials(firstName: string | null, lastName: string | null, email: string | null): string {
  if (firstName && lastName) {
    return (firstName[0] + lastName[0]).toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  return email?.charAt(0).toUpperCase() || '?';
}

function formatDueDate(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Overdue';
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
}

function isDueDateOverdue(dateStr: string): boolean {
  return new Date(dateStr) < new Date();
}

const TaskCard: React.FC<TaskCardProps> = ({ task, index, onClick }) => {
  return (
    <Draggable draggableId={task.id} index={index}>
      {(provided, snapshot) => (
        <div
          ref={provided.innerRef}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          className={cn(
            "bg-card border rounded-lg p-3 mb-2 cursor-pointer transition-all duration-200",
            "hover:-translate-y-0.5 hover:shadow-md hover:border-primary/20",
            snapshot.isDragging && "shadow-lg ring-2 ring-primary/20"
          )}
          onClick={() => onClick(task)}
        >
          <div className="space-y-2">
            {/* Title */}
            <p className="text-sm font-medium leading-tight line-clamp-2">
              {task.title}
            </p>

            {/* Priority + Labels row */}
            <div className="flex items-center gap-1.5 flex-wrap">
              <PriorityBadge priority={task.priority} />
              {task.labels && task.labels.split(',').map((label) => (
                <span
                  key={label.trim()}
                  className="inline-flex items-center rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground"
                >
                  {label.trim()}
                </span>
              ))}
            </div>

            {/* Footer: assignee, due date, comments */}
            <div className="flex items-center justify-between pt-1">
              <div className="flex items-center gap-2">
                {task.assigneeId && (
                  <div
                    className="flex h-6 w-6 items-center justify-center rounded-full bg-primary/10 text-primary text-[10px] font-medium"
                    title={`${task.assigneeFirstName || ''} ${task.assigneeLastName || ''}`.trim()}
                  >
                    {getInitials(task.assigneeFirstName, task.assigneeLastName, task.assigneeEmail)}
                  </div>
                )}
                {task.dueDate && (
                  <div
                    className={cn(
                      "flex items-center gap-1 text-[11px]",
                      isDueDateOverdue(task.dueDate)
                        ? "text-destructive"
                        : "text-muted-foreground"
                    )}
                  >
                    <Calendar className="h-3 w-3" />
                    {formatDueDate(task.dueDate)}
                  </div>
                )}
              </div>
              {task.commentCount > 0 && (
                <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
                  <MessageSquare className="h-3 w-3" />
                  {task.commentCount}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Draggable>
  );
};

export default TaskCard;
